import { Button, ErrorBanner } from "frontend-kit/components/index.js";
import { loadBackendConfig, postFeedback } from "./gateway-api.js";
import { loadParticipant } from "./participant-store.js";

const CATEGORIES = [
  { value: "bug", label: "Bug" },
  { value: "ux", label: "UX" },
  { value: "idea", label: "Idea" },
  { value: "other", label: "Other" },
];

/**
 * Build the context bag sent with feedback. Kept small - gateway caps at 8 KiB.
 * @param {{ name: string, id?: number }} route
 */
export function buildFeedbackContext(route) {
  const participant = loadParticipant();
  return {
    page: location.hash || "#/events",
    href: location.href,
    route: route?.name ?? null,
    market_id: route?.name === "detail" && Number.isFinite(route.id) ? route.id : null,
    participant_id: participant?.participant_id ?? null,
    participant_status: participant?.status ?? null,
    has_trading_key: Boolean(participant?.trading_commitment_hex),
    user_agent: navigator.userAgent,
    language: navigator.language ?? null,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? null,
    offline: typeof navigator.onLine === "boolean" ? !navigator.onLine : null,
    sent_at: new Date().toISOString(),
  };
}

/**
 * Header feedback form (popover body).
 *
 * Visibility is gated in `main.js` to the contract-owner wallet (same as
 * Create). When market creation opens beyond the deployer, widen that gate
 * there - this panel itself stays usable for any caller.
 *
 * @param {{ route: { name: string, id?: number }, onDone?: () => void }} opts
 */
export function renderFeedbackPanel({ route, onDone }) {
  const root = document.createElement("div");
  root.className = "feedback-panel dt-card";

  const hint = document.createElement("p");
  hint.className = "hint feedback-hint";
  hint.textContent =
    "Tell us what's broken or confusing. We include the page you're on (and a few browser details) so we can reproduce it.";
  root.append(hint);

  const form = document.createElement("form");
  form.className = "feedback-form";
  form.noValidate = true;

  const catLabel = document.createElement("label");
  catLabel.className = "form-label";
  catLabel.htmlFor = "pm-feedback-category";
  catLabel.textContent = "Type";

  const cat = document.createElement("select");
  cat.id = "pm-feedback-category";
  cat.className = "form-control";
  cat.name = "category";
  for (const opt of CATEGORIES) {
    const o = document.createElement("option");
    o.value = opt.value;
    o.textContent = opt.label;
    if (opt.value === "other") o.selected = true;
    cat.append(o);
  }

  const msgLabel = document.createElement("label");
  msgLabel.className = "form-label";
  msgLabel.htmlFor = "pm-feedback-message";
  msgLabel.textContent = "Feedback";

  const ta = document.createElement("textarea");
  ta.id = "pm-feedback-message";
  ta.className = "form-control feedback-textarea";
  ta.name = "message";
  ta.rows = 5;
  ta.maxLength = 4000;
  ta.required = true;
  ta.placeholder = "What happened? What did you expect?";

  const meta = document.createElement("p");
  meta.className = "hint feedback-meta";
  const pageLabel =
    route?.name === "detail" && route.id != null
      ? `Event #${route.id}`
      : route?.name
        ? String(route.name)
        : "this page";
  meta.textContent = `Sending from ${pageLabel}`;

  const actions = document.createElement("div");
  actions.className = "feedback-actions";

  const status = document.createElement("div");
  status.className = "feedback-status";

  let submitting = false;

  const submitBtn = Button({
    label: "Send feedback",
    variant: "primary",
  });
  submitBtn.type = "submit";

  actions.append(submitBtn);
  form.append(catLabel, cat, msgLabel, ta, meta, actions);
  root.append(form, status);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (submitting) return;
    const message = ta.value.trim();
    if (message.length < 3) {
      status.replaceChildren(ErrorBanner({ message: "Please write a bit more (at least 3 characters)." }));
      ta.focus();
      return;
    }
    submitting = true;
    submitBtn.disabled = true;
    status.replaceChildren();
    const pending = document.createElement("p");
    pending.className = "hint";
    pending.textContent = "Sending…";
    status.append(pending);

    try {
      const cfg = loadBackendConfig();
      await postFeedback(cfg, {
        message,
        category: cat.value || "other",
        context: buildFeedbackContext(route),
      });
      status.replaceChildren();
      const ok = document.createElement("p");
      ok.className = "feedback-thanks";
      ok.textContent = "Thanks - we got it.";
      status.append(ok);
      ta.value = "";
      cat.value = "other";
      onDone?.();
    } catch (err) {
      status.replaceChildren(
        ErrorBanner({ message: err?.message || String(err) || "Could not send feedback." })
      );
    } finally {
      submitting = false;
      submitBtn.disabled = false;
    }
  });

  queueMicrotask(() => ta.focus());
  return root;
}
